import {
  ASSET_CHANGE_SITUATION_TYPES,
  Button,
  calculatePaginationTotalPages,
  Checkbox,
  ConfirmModal,
  DEFAULT_PAGE_SIZE,
  FeatureFlagNames,
  FEATURES_ID,
  FixedAssetsService,
  getNs,
  messageSuccess,
  openFeatureSlice,
  Spin,
  Tooltip,
} from 'aa_common/front-end'
import { Pagination } from 'components/molecules'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchAssetEventsReport, fetchSummaryFixedAssetEvents } from 'store/accounting/assetEventsReport/actions'
import { selectAssetEventsReport } from 'store/accounting/assetEventsReport/selectors'
import { selectLoadingApp } from 'store/app/selectors'

import { AcquisitionEventView } from './AcquisitionEventView'
import { DividingEventView } from './DividingEventView'
import { ImpairmentEventView } from './ImpairmentEventView'
import { MovementEventView } from './MovementEventView'
import { RetirementEventView } from './RetirementEventView'
import { SaleEventView } from './SaleEventView'
import { SwitchEventView } from './SwitchEventView'

import {
  assetEventListReportSlice,
  setDetailMode,
  setError,
  setErrorIds,
  unSelectAllRowOnPage,
} from 'pages/accounting/AssetEventListReportPage/store'
import { StyledErrorWrapper, StyledHeader, StyledTable, StyledTableHeader, StyledWrapper } from './styles'
import useUserPermission from 'lib/hooks/useUserPermission'
import { CollectiveCheckbox } from './collective-checkbox'
import { useShallow } from 'zustand/react/shallow'
import { useGetSelectedEventOnPage } from 'components/organisms/accounting/AssetEventListReport/hooks'
import { get, isEqual } from 'lodash'
import { useLongPolling } from 'hooks/useLongPolling'
import ErrorsList from 'aa_common/front-end/components/errors-list'
import { useCheckEventAtClosingMonth } from 'components/organisms/accounting/AssetEventListReport/hooks/useCheckEventAtClosingMonth'
import LinkLabel from 'aa_common/front-end/components/link-label'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { URL_REOPEN_MONTHLY_GUIDE } from 'constants/app'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { selectTermData } from 'store/settings/accountingPeriod/selectors'

const { t, useNsReady } = getNs([
  'common',
  'components/organisms/assets/asset-detail',
  'components/organisms/accounting/monthly-closing-asset-event-list',
])

export function AssetEventListReport({ eventType }: Props) {
  useNsReady()
  const dispatch = useDispatch()

  const [pageNum, setPageNum] = useState(1)
  const [visible, setVisible] = useState(false)
  const { data: assetEvents } = useSelector(selectAssetEventsReport, isEqual)
  const loadingApp = useSelector(selectLoadingApp, isEqual)
  const termData = useSelector(selectTermData, isEqual)?.data

  const assetEventListReportData = assetEventListReportSlice.useSlice(useShallow(state => state.data))
  const currentTermMonth = termData?.term_months?.find(
    (termMonth: any) => termMonth.id === assetEventListReportData?.termMonthId
  )

  const { selectedEvents, selectedEventIds } = useGetSelectedEventOnPage(eventType)
  const remoteFlags = openFeatureSlice.useSlice()

  const isRemoteFlagsAllowed =
    (remoteFlags[FeatureFlagNames.CollectionViewBulkChangeMovement] &&
      eventType === ASSET_CHANGE_SITUATION_TYPES.MOVEMENT) ||
    (remoteFlags[FeatureFlagNames.CollectionViewBulkChangeRetirement] &&
      eventType === ASSET_CHANGE_SITUATION_TYPES.RETIREMENT)

  const handleChangePage = (value: number) => {
    setPageNum(value)
  }

  const { checkClosingMonthEvent } = useCheckEventAtClosingMonth({ records: selectedEvents, eventType: eventType })
  const featureId = useMemo(() => {
    switch (eventType) {
      //Only check Movement and Retire in this sprint(84)
      case ASSET_CHANGE_SITUATION_TYPES.RETIREMENT:
        return FEATURES_ID.RETIRE
      case ASSET_CHANGE_SITUATION_TYPES.MOVEMENT:
        return FEATURES_ID.MOVEMENT
      case ASSET_CHANGE_SITUATION_TYPES.SELL:
        return FEATURES_ID.SALE
      case ASSET_CHANGE_SITUATION_TYPES.IMPAIRMENT:
        return FEATURES_ID.IMPAIRMENT
      default:
        return FEATURES_ID.REPORT_ACQUISITION_TRANSFER
    }
  }, [eventType])

  const reFreshData = useCallback(() => {
    assetEventListReportData?.termMonthId &&
      dispatch(fetchSummaryFixedAssetEvents(assetEventListReportData?.termMonthId))
  }, [dispatch, assetEventListReportData?.termMonthId])

  const permissions = useUserPermission(featureId)
  const assetsData = useMemo(() => {
    return assetEvents?.data?.map((item: any) =>
      assetEventListReportData?.errorIds?.includes(item?.id?.toString()) ? { ...item, highlight: true } : item
    )
  }, [assetEvents?.data, assetEventListReportData?.errorIds])

  const renderedEventList = () => {
    if (!assetsData.length) return null

    switch (eventType) {
      case ASSET_CHANGE_SITUATION_TYPES.ACQUISITION:
        return <AcquisitionEventView assets={assetsData} />
      case ASSET_CHANGE_SITUATION_TYPES.RETIREMENT:
        return <RetirementEventView assets={assetsData} />
      case ASSET_CHANGE_SITUATION_TYPES.MOVEMENT:
        return <MovementEventView assets={assetsData} />
      case ASSET_CHANGE_SITUATION_TYPES.SELL:
        return <SaleEventView assets={assetsData} />
      case ASSET_CHANGE_SITUATION_TYPES.SWITCH:
        return <SwitchEventView assets={assetsData} />
      case ASSET_CHANGE_SITUATION_TYPES.IMPAIRMENT:
        return <ImpairmentEventView assets={assetsData} />
      case ASSET_CHANGE_SITUATION_TYPES.DIVIDING:
        return <DividingEventView assets={assetsData} />
      default:
        return null
    }
  }

  const fetchAssetEvents = useCallback(() => {
    if (assetEventListReportData?.termMonthId && eventType) {
      dispatch(fetchAssetEventsReport(assetEventListReportData?.termMonthId, eventType, pageNum, DEFAULT_PAGE_SIZE))
    }
  }, [assetEventListReportData?.termMonthId, pageNum, dispatch, eventType])

  useEffect(() => {
    fetchAssetEvents()
  }, [fetchAssetEvents])

  const isHavePermission = React.useMemo(() => {
    return permissions && permissions.permissions.isCRUD
  }, [permissions])

  const onSuccess = () => {
    messageSuccess(t('messages.cancel_success'))
    reFreshData()
    setPageNum(1)
    fetchAssetEvents()
    unSelectAllRowOnPage(selectedEventIds)
  }
  const onError = (errorIds: string[], error: string) => {
    setError(error)
    setVisible(false)
    setErrorIds(errorIds)
  }

  const cancelEventPolling = useLongPolling('cancelEventPolling', {
    onError,
    onSuccess,
    isShowToastErrorMessage: false,
  })
  const submitDeleteEvent = async () => {
    setError(null)
    if (!checkClosingMonthEvent.checkEventInClosingMonth) {
      const bodyDelete = selectedEvents.map(function (item: any) {
        return { biid: item.biid, id: item.id }
      })
      const res = await FixedAssetsService.bulkChangeDeleteDelete({ data: bodyDelete })
      res?.data?.id && cancelEventPolling.setJobId(res.data.id)
      setVisible(false)
    } else {
      setError('true')
      setVisible(false)
      setErrorIds(checkClosingMonthEvent.ids)
    }
  }

  const cancelDelete = () => {
    setVisible(false)
  }

  const getCurrentMonth = () => {
    const { month } = currentTermMonth || {}
    const { year } = termData || {}
    return year && month && `${year}/${month.toString().padStart(2, '0')}`
  }

  const hasSelectedItem = selectedEvents?.length > 0
  return (
    <Spin loading={loadingApp || cancelEventPolling.loading}>
      <StyledWrapper>
        {isRemoteFlagsAllowed && (
          <>
            <div className="cancel">
              <Tooltip content={isHavePermission ? undefined : t('actions.no_permission')}>
                <Button
                  color="grey"
                  style={{ color: isHavePermission && hasSelectedItem ? '#EC4949' : '#919191' }}
                  disabled={!isHavePermission || !hasSelectedItem}
                  onClick={() => selectedEvents?.length > 0 && setVisible(true)}
                >
                  {t('actions.cancel_bulk_change_event')}
                </Button>
              </Tooltip>
            </div>
            {selectedEvents?.length > 0 && (
              <ConfirmModal
                className="delete-event-modal"
                message={t('msg_confirm_cancel_event', {
                  date: getCurrentMonth(),
                  eventType: get(selectedEvents[0], ['change_situation', 'name_jp']),
                  count: selectedEvents?.length,
                })}
                visible={visible}
                onOK={submitDeleteEvent}
                onCancel={cancelDelete}
                okText={t('actions.delete')}
              />
            )}
            {assetEventListReportData?.error && (
              <StyledErrorWrapper>
                <ErrorsList
                  title={
                    checkClosingMonthEvent.checkEventInClosingMonth ? (
                      <div>
                        {t('msg_err_cancel_event_is_closed')}
                        <LinkLabel target="_blank" href={URL_REOPEN_MONTHLY_GUIDE}>
                          {t('actions.click_here_for_details')}
                          <FontAwesomeIcon style={{ marginLeft: 5 }} icon={faExternalLinkAlt} />
                        </LinkLabel>
                      </div>
                    ) : (
                      <div>{assetEventListReportData?.error}</div>
                    )
                  }
                  style={{ color: '#303030' }}
                />
              </StyledErrorWrapper>
            )}
          </>
        )}
        {assetEvents && (
          <>
            <StyledHeader>
              <>
                <div className="left-side">{isRemoteFlagsAllowed && <CollectiveCheckbox eventType={eventType} />}</div>
                <div className="right-side">
                  {isRemoteFlagsAllowed && (
                    <Checkbox
                      name="detail-display"
                      checked={assetEventListReportData?.isDetailMode}
                      onChange={({ checked }) => setDetailMode(checked)}
                    >
                      {t('detail_display')}
                    </Checkbox>
                  )}
                  <Pagination
                    name="fixed-asset"
                    value={pageNum}
                    totalPages={calculatePaginationTotalPages(assetEvents.total, DEFAULT_PAGE_SIZE) || 0}
                    totalRecords={assetEvents.total || 0}
                    onPageChanged={handleChangePage}
                  />
                </div>
              </>
            </StyledHeader>
            <StyledTable>
              <StyledTableHeader>
                {isRemoteFlagsAllowed && <div className="checkbox-column"></div>}
                <div className="status">{t('journal_linkage')}</div>
                <div className="photo">{t('photo')}</div>
                <div className="asset-code">{t('asset_code')}</div>
                <div className="asset-name">{t('asset_name')}</div>
              </StyledTableHeader>
              {renderedEventList()}
            </StyledTable>
          </>
        )}
      </StyledWrapper>
    </Spin>
  )
}

type Props = {
  eventType?: ASSET_CHANGE_SITUATION_TYPES
}
